import { useEffect, useId, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useTests } from '@/hooks/coaching/useTests'
import type { LcTestKindKey, LcTestSummaryView } from '@/types/coaching'

interface Props {
  learnerId: number
  kind: LcTestKindKey
  /** 모달 부제용 원생 이름(옵션). */
  learnerName?: string
  onClose: () => void
}

const KIND_KEY: Record<LcTestKindKey, 'chapter' | 'cumulative'> = {
  CHAPTER: 'chapter',
  CHAPTER_COMPREHENSIVE: 'cumulative',
}

/** 'YYYY-MM-DDTHH:mm:ss' ISO 앞 10자(완료일자). null/빈 문자열은 호출측에서 dateUnknown 처리. */
function toDateOnly(iso: string): string {
  return iso.slice(0, 10)
}

/**
 * 완료 챕터/누적 테스트 평면 목록(완료일자/점수/리포트) — 원생 상세 학습상태 평균 클릭 진입(P5).
 * 데이터는 `useTests`(코칭 Phase5) 재사용 — `data.tests` 중 kind+COMPLETED 만 필터.
 * a11y/포털은 EnrollmentHistoryModal 패턴 미러(조상 transform dim 갇힘 회피 위해 document.body 포털).
 */
export function CompletedTestListModal({ learnerId, kind, learnerName, onClose }: Props) {
  const { t } = useTranslation('admin')
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const tk = (s: string) => t(`students.detail.completedTests.${s}`)
  const kindKey = KIND_KEY[kind]

  const { data } = useTests(learnerId)

  // 완료 + 동일 kind 필터, completedAt 내림차순(없거나 파싱불가는 마지막)·tiebreak chapterOrdinal 오름차순.
  // 시각 비교는 Date.parse 로 포맷 비의존(ISO 문자열 사전식 비교의 포맷 드리프트 footgun 회피).
  const rows = useMemo<LcTestSummaryView[]>(() => {
    const filtered = (data?.tests ?? []).filter((tt) => tt.kind === kind && tt.status === 'COMPLETED')
    const ms = (iso: string | null): number => {
      const n = iso ? Date.parse(iso) : NaN
      return Number.isNaN(n) ? -Infinity : n // 무효/null 은 가장 작게 → 내림차순에서 마지막
    }
    return [...filtered].sort((a, b) => {
      const diff = ms(b.completedAt) - ms(a.completedAt)
      return diff !== 0 ? diff : a.chapterOrdinal - b.chapterOrdinal
    })
  }, [data?.tests, kind])

  // 마운트 1회: Escape 바인딩 + 패널 포커스, 언마운트 시 직전 포커스(트리거) 복원.
  useEffect(() => {
    const prevFocused = document.activeElement as HTMLElement | null
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current()
    }
    document.addEventListener('keydown', onKeyDown)
    panelRef.current?.focus()
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      prevFocused?.focus?.()
    }
  }, [])

  const openReport = (testId: number) => {
    const path = kind === 'CHAPTER'
      ? `/admin/coaching/chapter-report/${learnerId}/${testId}`
      : `/admin/coaching/test-report/${learnerId}/${testId}`
    window.open(path, '_blank', 'noopener,noreferrer')
  }

  const body = (
    <div
      data-testid="completed-tests-backdrop"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 id={titleId} className="text-lg font-bold text-gray-900">
              {tk(`title.${kindKey}`)}
            </h2>
            {learnerName && (
              <p className="mt-0.5 text-sm text-gray-500">
                {t('students.detail.completedTests.subtitle', { name: learnerName })}
              </p>
            )}
          </div>
        </div>

        {rows.length === 0 ? (
          <p className="py-12 text-center text-sm text-gray-400">{tk(`empty.${kindKey}`)}</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {(['completedAt', 'score', 'report'] as const).map((col) => (
                    <th
                      key={col}
                      scope="col"
                      className="whitespace-nowrap px-4 py-2 text-left text-xs font-medium uppercase text-gray-500"
                    >
                      {tk(`col.${col}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => (
                  <tr key={row.testId} className="text-gray-700">
                    <td className="whitespace-nowrap px-4 py-2">
                      {row.completedAt ? toDateOnly(row.completedAt) : tk('dateUnknown')}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 font-medium text-gray-900">
                      {/* COMPLETED 는 항상 score 보유 — null 은 방어적 분기(번역 키 비결합, 리터럴 대시). */}
                      {row.score != null
                        ? t('students.detail.completedTests.score', { score: row.score })
                        : '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2">
                      <button
                        type="button"
                        onClick={() => openReport(row.testId)}
                        className="rounded-lg border border-gray-200 px-3 py-1 text-xs text-primary-700 hover:bg-gray-50"
                      >
                        {tk('reportLink')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            {tk('close')}
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(body, document.body)
}
